import { addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, serverTimestamp, Timestamp, where } from 'firebase/firestore';

import { db } from '@/config/firebase';
import type {
  CreateHealthPromotionImportPayload,
  HealthPromotionImportDocument,
  HealthPromotionImportTotals,
  HealthPromotionSourceFile,
} from './healthPromotionTypes';

const HEALTH_PROMOTION_IMPORTS_COLLECTION = 'expenses_health_promotion_imports';

export async function createHealthPromotionImport(
  provider: string,
  payload: CreateHealthPromotionImportPayload,
  user: { uid: string; email?: string | null; displayName?: string | null },
): Promise<HealthPromotionImportDocument> {
  const docRef = await addDoc(collection(db, HEALTH_PROMOTION_IMPORTS_COLLECTION), {
    provider,
    billingPeriod: payload.billingPeriod ?? null,
    totals: payload.totals ?? { dependents: 0, feeCount: 0, amount: 0 },
    billingValues: payload.billingValues ?? { collaborators: 0, dependents: 0 },
    sourceFiles: payload.sourceFiles ?? [],
    recordsPreview: payload.recordsPreview ?? [],
    createdAt: serverTimestamp(),
    createdByUid: user.uid,
    createdByEmail: user.email ?? null,
    createdByName: user.displayName ?? null,
    audit: payload.audit ?? [],
  });

  return {
    id: docRef.id,
    provider,
    billingPeriod: payload.billingPeriod ?? null,
    totals: payload.totals ?? { dependents: 0, feeCount: 0, amount: 0 },
    billingValues: payload.billingValues ?? { collaborators: 0, dependents: 0 },
    sourceFiles: payload.sourceFiles ?? [],
    recordsPreview: payload.recordsPreview ?? [],
    createdAt: new Date(),
    createdByUid: user.uid,
    createdByEmail: user.email ?? null,
    createdByName: user.displayName ?? null,
    audit: payload.audit ?? [],
  };
}

export async function deleteHealthPromotionImportById(importId: string): Promise<void> {
  await deleteDoc(doc(db, HEALTH_PROMOTION_IMPORTS_COLLECTION, importId));
}

function mapTotals(data: any): HealthPromotionImportTotals {
  if (!data || typeof data !== 'object') {
    return { dependents: 0, feeCount: 0, amount: 0 };
  }
  return {
    dependents: typeof data.dependents === 'number' ? data.dependents : 0,
    feeCount: typeof data.feeCount === 'number' ? data.feeCount : 0,
    amount: typeof data.amount === 'number' ? data.amount : 0,
  };
}

function mapSourceFiles(data: any): HealthPromotionSourceFile[] {
  if (!Array.isArray(data)) return [];
  return data
    .map((item) => ({
      name: typeof item?.name === 'string' ? item.name : 'unknown',
      size: typeof item?.size === 'number' ? item.size : 0,
      contentType: typeof item?.contentType === 'string' ? item.contentType : null,
    }))
    .filter((file) => file.name);
}

function mapDocument(docSnapshot: any): HealthPromotionImportDocument | null {
  const data = docSnapshot.data?.() ?? docSnapshot.data;
  if (!data) return null;
  const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : null;
  return {
    id: docSnapshot.id,
    provider: typeof data.provider === 'string' ? data.provider : '',
    billingPeriod: typeof data.billingPeriod === 'string' ? data.billingPeriod : null,
    totals: mapTotals(data.totals),
    billingValues: {
      collaborators: typeof data?.billingValues?.collaborators === 'number' ? data.billingValues.collaborators : 0,
      dependents: typeof data?.billingValues?.dependents === 'number' ? data.billingValues.dependents : 0,
    },
    sourceFiles: mapSourceFiles(data.sourceFiles),
    recordsPreview: Array.isArray(data.recordsPreview) ? (data.recordsPreview as any[]) : [],
    createdAt,
    createdByUid: typeof data.createdByUid === 'string' ? data.createdByUid : '',
    createdByEmail: typeof data.createdByEmail === 'string' ? data.createdByEmail : null,
    createdByName: typeof data.createdByName === 'string' ? data.createdByName : null,
    audit: Array.isArray(data.audit) ? data.audit : [],
  };
}

export async function listHealthPromotionImports(): Promise<HealthPromotionImportDocument[]> {
  const importsQuery = query(collection(db, HEALTH_PROMOTION_IMPORTS_COLLECTION), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(importsQuery);
  return snapshot.docs.map(mapDocument).filter(Boolean) as HealthPromotionImportDocument[];
}

export async function listHealthPromotionImportsByCompetence(competence: string): Promise<HealthPromotionImportDocument[]> {
  const importsQuery = query(
    collection(db, HEALTH_PROMOTION_IMPORTS_COLLECTION),
    where('billingPeriod', '==', competence),
  );
  const snapshot = await getDocs(importsQuery);
  return (snapshot.docs.map(mapDocument).filter(Boolean) as HealthPromotionImportDocument[]).sort(
    (a, b) => (b.createdAt?.getTime?.() ?? 0) - (a.createdAt?.getTime?.() ?? 0),
  );
}
